// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import PortPilotLoginPage from "./pages/PortPilotLoginPage";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import StaffRoute from "./components/StaffRoute";
import Sidebar from "./components/layout/Sidebar";
import Dashboard from "./pages/Dashboard";
import PoRoutingDashboard from "./pages/PoRoutingDashboard";
import POs from "./pages/POs";
import POsSandbox from "./pages/POsSandbox";
import Routing from "./pages/Routing";
import RoutingStatus from "./pages/RoutingStatus";
import RoutingXPOs from "./pages/RoutingXPOs";
import RoutingStatusSandbox from "./pages/RoutingStatusSandbox";
import MRP from "./pages/MRP";
import MrpContainerDetails from "./pages/MrpContainerDetails";
import MrpContainerDashboard from "./pages/MrpContainerDashboard";
import MrpProducts from "./pages/MrpProducts";
import Pricing from "./pages/Pricing";
import MrpContainerDetailsPivot from "./pages/MrpContainerDetailsPivot";
import MRPInventoryTab from "./pages/MRPInventoryTab";
import MRPInventoryPivot from "./pages/MRPInventoryPivot";
import WalmartLadderPlan from "./pages/WalmartLadderPlan";
import PendingWMSEntry from "./pages/PendingWMSEntry";
import PendingWMSEntryPivot from "./pages/PendingWMSEntryPivot";
import MyContainers from "./pages/MyContainers";
import NoAccess from "./pages/NoAccess";
import Drayage from "./pages/Drayage";
import Warehouse from "./pages/Warehouse";
import Customers from "./pages/Customers";
import ShipPointManagement from "./pages/ShipPointManagement";
import Users from "./pages/Users";
import AdminHome from "./pages/AdminHome";
import AdminLogs from "./pages/AdminLogs";
import AdminApiLogs from "./pages/AdminApiLogs";
import AdminIngestWarnings from "./pages/AdminIngestWarnings";
import AdminProductSuggestions from "./pages/AdminProductSuggestions";
import AdminPricingOverrides from "./pages/AdminPricingOverrides";
import AdminRoles from "./pages/AdminRoles";
import CustApiMappings from "./pages/CustApiMappings";
import Snapshots from "./pages/Snapshots";
import ApiManager from "./pages/ApiManager";
import WalmartDcAddressBook from "./pages/WalmartDcAddressBook";
import WayliRawVessels from "./pages/WayliRawVessels";
import WayliRawVesselContainers from "./pages/WayliRawVesselContainers";
import WayliRawVesselConsignees from "./pages/WayliRawVesselConsignees";
import Developers from "./pages/Developers";
import EdiInbox from "./pages/EdiInbox";
import Edi997Drafts from "./pages/Edi997Drafts";
import EdiPoStatus from "./pages/EdiPoStatus";
import Walmart856 from "./pages/Walmart856";
import Walmart810 from "./pages/Walmart810";
import { PermissionsProvider, usePermissions } from "./utils/permissions";
import { readAuthSafe, refreshUserProfile } from "./utils/auth";
import {
  getAllowedPagesForRole,
  resolveDefaultRoute,
  SAFE_FALLBACK_ROUTE,
} from "./utils/defaultPage";

function AppShell() {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("pp_sidebar_collapsed") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("pp_sidebar_collapsed", collapsed ? "1" : "0"); }
    catch {}
  }, [collapsed]);

  return (
    // h-screen + overflow-hiddenA_A???AcA?A?AcA?A?A?"A?AcA????AA,A?A?A?A?A???1???A_A????A?A?A?A"Ar??oA?A?A3A?A?A' main A?A?A?
    <div className="flex h-screen overflow-hidden">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(v => !v)} />
      <main className="flex-1 overflow-auto p-3">
        <Outlet />
      </main>
    </div>
  );
}
export { AppShell };

export function RootRedirect() {
  const auth = readAuthSafe();
  const { getLevel } = usePermissions();
  const [resolvedRoute, setResolvedRoute] = useState(SAFE_FALLBACK_ROUTE);
  const [ready, setReady] = useState(false);
  const authEmail = auth?.user?.email || "";
  const authRole = auth?.user?.role || "";

  useEffect(() => {
    if (!authEmail) return undefined;
    let cancelled = false;

    const computeFallbackRoute = (overrides = {}) => {
      const currentUser = readAuthSafe()?.user || { email: authEmail, role: authRole };
      const allowedPages = getAllowedPagesForRole(currentUser.role, getLevel);
      const route =
        resolveDefaultRoute({
          user: currentUser,
          role: currentUser.role,
          getLevel,
          userDefaultPageId: overrides.user_default_page_id ?? currentUser.default_page_id,
          roleDefaultPageId: overrides.role_default_page_id ?? currentUser.role_default_page_id,
          allowedPagesOverride: allowedPages,
        }) || SAFE_FALLBACK_ROUTE;
      return route;
    };

    async function resolveRoute() {
      try {
        const profile = await refreshUserProfile();
        if (cancelled) return;
        if (profile?.landing_route) {
          setResolvedRoute(profile.landing_route);
        } else {
          setResolvedRoute(computeFallbackRoute(profile));
        }
      } catch (err) {
        if (cancelled) return;
        setResolvedRoute(computeFallbackRoute());
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    resolveRoute();
    return () => {
      cancelled = true;
    };
  }, [authEmail, authRole, getLevel]);

  if (!authEmail) {
    return <Navigate to="/login" replace />;
  }

  if (!ready) {
    return null;
  }

  return <Navigate to={resolvedRoute || SAFE_FALLBACK_ROUTE} replace />;
}

export default function App() {
  return (
    <PermissionsProvider>
      <BrowserRouter>
        <Routes>
          {/* AcA?A?A"A"A-A?A???A?A???~A?,?A?A????A? */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<PortPilotLoginPage />} />
          <Route path="/developers" element={<Developers />} />

          {/* A????,?A?"???A?,?A?A????A?A?A_?'AcA?A?AcA?A?Ac?'A?A???A"A?A??'AA,?,?A??,???1 AppShellA_A?E+=> Sidebar A?A?A?A?A,A?A?,??oAA,?,?A?A?A?A_A???? */}
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            {/* A??"??? A?,?A?A????A?A?A_?'AcA?A?A"A"A-AcA?A?A_A???My Containers */}
            {/* index redirect removed */}

            {/* A??"??? AA?A?A???A"A"?,????AA?A?A?A?A_AcA??_A???A? Dashboard */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/po-routing" element={<PoRoutingDashboard />} />
            <Route path="/pos" element={<POs />} />
            <Route path="/pos/pending-wms-entry" element={<PendingWMSEntry />} />
            <Route path="/pos/pending-wms-entry/pivot" element={<PendingWMSEntryPivot />} />
            <Route path="/toolbox/pos" element={<POsSandbox />} />
            <Route path="/routing" element={<Routing />} />
            <Route
              path="/routing/routing-x-pos"
              element={
                <StaffRoute pageId="routing-x-pos">
                  <RoutingXPOs />
                </StaffRoute>
              }
            />
            <Route path="/routing/routing-status" element={<RoutingStatus />} />
            <Route path="/toolbox/routing-status" element={<RoutingStatusSandbox />} />
            <Route path="/mrp/mrp-aio" element={<MRP />} />
            <Route path="/mrp/container-dashboard" element={<MrpContainerDashboard />} />
            <Route path="/mrp/container-details" element={<MrpContainerDetails />} />
            <Route path="/mrp/container-details/pivot" element={<MrpContainerDetailsPivot />} />
            <Route path="/mrp/product" element={<MrpProducts />} />
            <Route path="/mrp/inventory" element={<MRPInventoryTab />} />
            <Route path="/mrp/inventory/pivot" element={<MRPInventoryPivot />} />
            <Route path="/mrp/walmart-ladder" element={<WalmartLadderPlan />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/api/wayli/raw-vessels" element={<WayliRawVessels />} />
            <Route path="/api/wayli/raw-vessel-containers" element={<WayliRawVesselContainers />} />
            <Route path="/api/wayli/raw-vessel-consignees" element={<WayliRawVesselConsignees />} />

            {/* A????AA?Ar?'AA,?,?A"E+A?AcA?A?AcA?A? */}
            <Route path="/my-containers" element={<MyContainers />} />
            <Route path="/edi/inbox" element={<StaffRoute pageId="edi-inbox"><EdiInbox /></StaffRoute>} />
            <Route path="/edi/997-drafts" element={<StaffRoute pageId="edi-997-drafts"><Edi997Drafts /></StaffRoute>} />
            <Route path="/edi/po-status" element={<StaffRoute pageId="edi-po-status"><EdiPoStatus /></StaffRoute>} />
            <Route path="/edi/856" element={<StaffRoute pageId="edi-856"><Walmart856 /></StaffRoute>} />
            <Route path="/edi/810" element={<StaffRoute pageId="edi-810"><Walmart810 /></StaffRoute>} />
            <Route path="/no-access" element={<NoAccess />} />
            <Route path="/mrp" element={<Navigate to="/mrp/mrp-aio" replace />} />

            {/* Admin A?A??,?A_A?E+A?A???Ac?,?A?A????A?A?A???" AdminRouteA_A???? */}
            <Route
              path="/admin"
              element={
                <AdminRoute pageId="admin-home">
                  <AdminHome />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <AdminRoute pageId="admin-roles">
                  <AdminRoles />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute pageId="admin-users">
                  <Users />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <AdminRoute pageId="admin-logs">
                  <AdminLogs />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/api-logs"
              element={
                <AdminRoute pageId="admin-api-logs">
                  <AdminApiLogs />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/pricing-overrides"
              element={
                <AdminRoute pageId="admin-pricing-overrides">
                  <AdminPricingOverrides />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/ingest-warnings"
              element={
                <AdminRoute pageId="admin-ingest-warnings">
                  <AdminIngestWarnings />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/product-suggestions"
              element={
                <AdminRoute pageId="admin-product-suggestions">
                  <AdminProductSuggestions />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/drayage"
              element={
                <StaffRoute pageId="admin-drayage">
                  <Drayage />
                </StaffRoute>
              }
            />
            <Route
              path="/admin/warehouse"
              element={
                <StaffRoute pageId="admin-warehouse">
                  <Warehouse />
                </StaffRoute>
              }
            />
            <Route
              path="/admin/ship-points"
              element={
                <StaffRoute pageId="admin-ship-points">
                  <ShipPointManagement />
                </StaffRoute>
              }
            />
            <Route
              path="/admin/customers"
              element={
                <StaffRoute pageId="admin-customers">
                  <Customers />
                </StaffRoute>
              }
            />
            <Route
              path="/admin/api-mappings"
              element={
                <AdminRoute pageId="admin-cust-api-mappings">
                  <CustApiMappings />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/api-manager"
              element={
                <AdminRoute pageId="admin-api-manager">
                  <ApiManager />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/walmart-dc-address-book"
              element={
                <AdminRoute pageId="admin-walmart-dc-address-book">
                  <WalmartDcAddressBook />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/snapshots"
              element={
                <AdminRoute pageId="admin-snapshots">
                  <Snapshots />
                </AdminRoute>
              }
            />

            {/* A"E+??A"A?A_A?A_??~A???A,A?ArA1A?,?AA"A????A?A???~A_A?E+A?A?A_A????,?A?A?A_A??1A?A?????A_A???? */}
            <Route path="/users" element={<Navigate to="/admin/users" replace />} />
            <Route path="/drayage" element={<Navigate to="/admin/drayage" replace />} />
            <Route path="/warehouse" element={<Navigate to="/admin/warehouse" replace />} />
            <Route path="/customers" element={<Navigate to="/admin/customers" replace />} />
          </Route>

          {/* A????AA?Ar?'AA,A?A??'A1Ac???A?A????zA"A?A_A?A_??~AA,?,?A?A_??1A?????_A?,?A?A????A? */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </PermissionsProvider>
  );
}
